/**
 * Thumbnail Studio — analyze topic/URL → AI overlay-text suggestions +
 * top-N YouTube reference thumbnails → live Fabric.js canvas editor.
 *
 * Route: /tools/design/thumbnail-studio
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as fabric from "fabric";
import {
  Wand2,
  Youtube,
  Download,
  Type as TypeIcon,
  Image as ImageIcon,
  Trash2,
  Sparkles,
  Palette,
  RotateCcw,
  Play,
  Copy as CopyIcon,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const FONT_OPTIONS = [
  "Impact",
  "Anton",
  "Bebas Neue",
  "Oswald",
  "Montserrat",
  "Arial Black",
  "Bangers",
];

const COLOR_PRESETS = [
  "#FFFFFF",
  "#000000",
  "#FFD400",
  "#EF4444",
  "#22C55E",
  "#3B82F6",
  "#A855F7",
  "#F97316",
];

export default function ThumbnailStudioPage() {
  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);

  const [input, setInput] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const [textColor, setTextColor] = useState("#FFFFFF");
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [fontFamily, setFontFamily] = useState("Impact");
  const [fontSize, setFontSize] = useState(120);

  // ── Init Fabric canvas once ─────────────────────────────────
  useEffect(() => {
    if (!canvasElRef.current || fabricRef.current) return;
    const c = new fabric.Canvas(canvasElRef.current, {
      width: 1280,
      height: 720,
      backgroundColor: "#111827",
      preserveObjectStacking: true,
    });
    fabricRef.current = c;
    // Placeholder text
    const hint = new fabric.Textbox("Add background or text →", {
      left: 640,
      top: 360,
      originX: "center",
      originY: "center",
      fontSize: 48,
      fill: "#9CA3AF",
      fontFamily: "Arial",
      selectable: false,
      evented: false,
    });
    c.add(hint);
    c.__hint = hint;
    c.renderAll();
    return () => {
      c.dispose();
      fabricRef.current = null;
    };
  }, []);

  const clearHint = () => {
    const c = fabricRef.current;
    if (c?.__hint) {
      c.remove(c.__hint);
      c.__hint = null;
    }
  };

  // ── Actions ─────────────────────────────────────────────────
  const analyze = async () => {
    if (!input.trim() || analyzing) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/thumbnail-studio/analyze`, {
        input: input.trim(),
        max_refs: 10,
      });
      setResult(res.data);
      toast.success(
        `Got ${res.data.references?.length || 0} references + ${res.data.suggestions?.length || 0} AI text ideas`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const applyReferenceBg = (url) => {
    const c = fabricRef.current;
    if (!c) return;
    // Use CORS-proxied load via HTMLImage
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const fImg = new fabric.Image(img, { selectable: false, evented: false });
      const scaleX = c.getWidth() / img.width;
      const scaleY = c.getHeight() / img.height;
      const scale = Math.max(scaleX, scaleY);
      fImg.scale(scale);
      fImg.set({ left: 0, top: 0 });
      // Remove old bg image (keep text)
      c.getObjects().forEach((o) => {
        if (o.__isBg) c.remove(o);
      });
      fImg.__isBg = true;
      c.add(fImg);
      c.sendObjectToBack(fImg);
      clearHint();
      c.renderAll();
      toast.success("Reference thumbnail loaded — edit it now!");
    };
    img.onerror = () => toast.error("Could not load thumbnail (CORS)");
    img.src = url;
  };

  const uploadBg = (file) => {
    const c = fabricRef.current;
    if (!c || !file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const fImg = new fabric.Image(img, { selectable: false, evented: false });
        const scale = Math.max(c.getWidth() / img.width, c.getHeight() / img.height);
        fImg.scale(scale);
        c.getObjects().forEach((o) => {
          if (o.__isBg) c.remove(o);
        });
        fImg.__isBg = true;
        c.add(fImg);
        c.sendObjectToBack(fImg);
        clearHint();
        c.renderAll();
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const addTextToCanvas = (text) => {
    const c = fabricRef.current;
    if (!c) return;
    const t = new fabric.Textbox(text.toUpperCase(), {
      left: 640,
      top: 360,
      originX: "center",
      originY: "center",
      fontSize,
      fontFamily,
      fontWeight: "900",
      fill: textColor,
      stroke: strokeColor,
      strokeWidth: 4,
      textAlign: "center",
      paintFirst: "stroke",
      shadow: "rgba(0,0,0,0.5) 4px 4px 8px",
    });
    clearHint();
    c.add(t);
    c.setActiveObject(t);
    c.renderAll();
  };

  const addBlankText = () => addTextToCanvas("YOUR TEXT");

  const deleteSelected = () => {
    const c = fabricRef.current;
    if (!c) return;
    const obj = c.getActiveObject();
    if (obj) {
      c.remove(obj);
      c.discardActiveObject();
      c.renderAll();
    }
  };

  const applyStyleToSelected = () => {
    const c = fabricRef.current;
    if (!c) return;
    const obj = c.getActiveObject();
    if (obj && obj.type === "textbox") {
      obj.set({
        fill: textColor,
        stroke: strokeColor,
        fontFamily,
        fontSize,
      });
      c.renderAll();
    } else {
      toast.info("Select a text object first");
    }
  };

  const resetCanvas = () => {
    const c = fabricRef.current;
    if (!c) return;
    c.clear();
    c.backgroundColor = "#111827";
    const hint = new fabric.Textbox("Add background or text →", {
      left: 640,
      top: 360,
      originX: "center",
      originY: "center",
      fontSize: 48,
      fill: "#9CA3AF",
      fontFamily: "Arial",
      selectable: false,
      evented: false,
    });
    c.add(hint);
    c.__hint = hint;
    c.renderAll();
  };

  const download = () => {
    const c = fabricRef.current;
    if (!c) return;
    // Temporarily remove hint if present
    if (c.__hint) {
      c.remove(c.__hint);
      c.__hint = null;
    }
    const dataUrl = c.toDataURL({ format: "png", quality: 1, multiplier: 1 });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `thumbnail_${Date.now()}.png`;
    a.click();
    toast.success("Downloaded 1280×720 thumbnail");
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied "${text}"`);
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-16">
      <PremiumNav />
      {/* Hero */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <p
            className="text-xs uppercase tracking-widest text-[#A855F7] font-semibold mb-3"
            data-testid="thumbnail-studio-eyebrow"
          >
            Design Suite · Thumbnail Studio
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            AI Thumbnail Studio
          </h1>
          <p className="text-base text-neutral-600 mt-3 max-w-2xl">
            Analyze any topic or YouTube URL — get 6 punchy AI overlay-text ideas,
            top-10 real reference thumbnails from YouTube, and edit live on a
            1280×720 canvas.
          </p>

          {/* Input */}
          <div className="mt-8 flex gap-3 flex-col sm:flex-row max-w-3xl">
            <div className="relative flex-1">
              <Youtube className="w-5 h-5 text-neutral-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="thumbnail-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && analyze()}
                placeholder="Paste a YouTube URL or type a topic (e.g. AI marketing in 2026)"
                className="pl-12 h-12 text-base bg-white border-neutral-300"
              />
            </div>
            <Button
              data-testid="thumbnail-analyze-btn"
              onClick={analyze}
              disabled={analyzing || !input.trim()}
              className="h-12 px-6 bg-[#111111] hover:bg-[#000] text-white"
            >
              {analyzing ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing…
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Analyze & Suggest
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <section className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* LEFT: Canvas + controls */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-[#111111]">Live Canvas · 1280×720</h2>
              <p className="text-sm text-neutral-500">Drag, resize, rotate — YouTube-safe dimensions.</p>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="thumbnail-add-text-btn"
                variant="outline"
                onClick={addBlankText}
                className="border-neutral-300"
              >
                <TypeIcon className="w-4 h-4 mr-2" />
                Add Text
              </Button>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="thumbnail-upload-bg"
                  onChange={(e) => uploadBg(e.target.files?.[0])}
                />
                <Button variant="outline" className="border-neutral-300" asChild>
                  <span>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Upload BG
                  </span>
                </Button>
              </label>
              <Button
                data-testid="thumbnail-download-btn"
                onClick={download}
                className="bg-[#A855F7] hover:bg-[#9333EA] text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PNG
              </Button>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm">
            <div className="overflow-auto">
              <canvas ref={canvasElRef} data-testid="thumbnail-canvas" />
            </div>
          </div>

          {/* Text style panel */}
          <Card className="mt-6 p-5 border-neutral-200">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-4 h-4 text-neutral-500" />
              <h3 className="text-sm font-semibold text-[#111111]">Text style (applies to selected text)</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="text-xs text-neutral-500 mb-1 block">Font</label>
                <select
                  data-testid="thumbnail-font-select"
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-neutral-500 mb-1 block">Size ({fontSize}px)</label>
                <input
                  data-testid="thumbnail-fontsize"
                  type="range"
                  min="40"
                  max="240"
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-500 mb-1 block">Fill</label>
                <div className="flex gap-1 flex-wrap">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={`fill-${c}`}
                      onClick={() => setTextColor(c)}
                      className={`w-6 h-6 rounded-full border-2 ${
                        textColor === c ? "border-[#A855F7]" : "border-neutral-300"
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-neutral-500 mb-1 block">Stroke</label>
                <div className="flex gap-1 flex-wrap">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={`stroke-${c}`}
                      onClick={() => setStrokeColor(c)}
                      className={`w-6 h-6 rounded-full border-2 ${
                        strokeColor === c ? "border-[#A855F7]" : "border-neutral-300"
                      }`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                data-testid="thumbnail-apply-style-btn"
                onClick={applyStyleToSelected}
                variant="outline"
                className="border-neutral-300"
              >
                Apply to selected
              </Button>
              <Button
                data-testid="thumbnail-delete-btn"
                onClick={deleteSelected}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete selected
              </Button>
              <Button
                data-testid="thumbnail-reset-btn"
                onClick={resetCanvas}
                variant="outline"
                className="border-neutral-300"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </Card>
        </div>

        {/* RIGHT: AI suggestions + references */}
        <aside className="space-y-6">
          {/* AI suggestions */}
          <Card className="p-5 border-neutral-200" data-testid="thumbnail-suggestions-panel">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#A855F7]" />
              <h3 className="text-sm font-semibold text-[#111111]">AI text ideas</h3>
              {result?.model && <Badge variant="secondary" className="ml-auto text-[10px]">{result.model}</Badge>}
            </div>
            {!result && !analyzing && (
              <p className="text-sm text-neutral-500">Run an analysis to see 6 catchy overlay-text ideas.</p>
            )}
            {analyzing && (
              <div className="text-sm text-neutral-500 flex items-center gap-2">
                <Sparkles className="w-4 h-4 animate-spin" />
                Thinking…
              </div>
            )}
            {result?.suggestions?.length > 0 && (
              <ul className="space-y-2" data-testid="thumbnail-suggestions-list">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 bg-neutral-50 rounded-lg px-3 py-2 group">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111111] truncate">{s.text}</p>
                      <p className="text-[11px] text-neutral-500 truncate">
                        {s.angle} · {s.hook_type}
                      </p>
                    </div>
                    <button
                      data-testid={`thumbnail-use-suggestion-${i}`}
                      onClick={() => addTextToCanvas(s.text)}
                      className="opacity-70 group-hover:opacity-100 text-[11px] text-[#A855F7] font-semibold hover:underline"
                      title="Add to canvas"
                    >
                      Use
                    </button>
                    <button
                      onClick={() => copyText(s.text)}
                      className="text-neutral-400 hover:text-neutral-700"
                      title="Copy"
                    >
                      <CopyIcon className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Reference thumbnails */}
          <Card className="p-5 border-neutral-200" data-testid="thumbnail-references-panel">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-[#A855F7]" />
              <h3 className="text-sm font-semibold text-[#111111]">Top reference thumbnails</h3>
              {result?.reference_count > 0 && (
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  {result.reference_count}
                </Badge>
              )}
            </div>
            {!result && !analyzing && (
              <p className="text-sm text-neutral-500">
                Analyze a topic to pull the top-performing YouTube thumbnails.
              </p>
            )}
            {result?.references?.length > 0 && (
              <div className="grid grid-cols-2 gap-2" data-testid="thumbnail-references-grid">
                {result.references.map((r, i) => (
                  <div
                    key={r.video_id}
                    className="group relative rounded-lg overflow-hidden border border-neutral-200"
                    data-testid={`thumbnail-ref-${i}`}
                  >
                    <img
                      src={r.thumbnail}
                      alt={r.title}
                      className="w-full aspect-video object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-1 p-2">
                      <button
                        data-testid={`thumbnail-use-ref-${i}`}
                        onClick={() => applyReferenceBg(r.thumbnail)}
                        className="text-[11px] bg-white text-black rounded-full px-3 py-1 font-semibold"
                      >
                        Use as BG
                      </button>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-white/90 hover:text-white flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        {r.views_display}
                      </a>
                    </div>
                    <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
                      {r.views_display}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </aside>
      </section>
      <PremiumFooter />
    </div>
  );
}
